import { useState, type FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useSubmitContactForm } from '../hooks/use-contact.js';
import { StorefrontErrorState } from '../../components/states.js';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_MAX = 120;
const SUBJECT_MAX = 150;
const MESSAGE_MAX = 4000;

interface FormValues {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const EMPTY_VALUES: FormValues = { name: '', email: '', subject: '', message: '' };

/**
 * Submits to `POST /api/v1/contact` (see `apps/worker/src/routes/v1/contact.ts`)
 * -- a real backend call, not a fake success state. A failed request shows
 * `StorefrontErrorState` with a retry, never a silent/optimistic "sent"
 * message.
 */
export function ContactForm() {
  const [values, setValues] = useState<FormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});
  const { mutate, isPending, isSuccess, isError, reset } = useSubmitContactForm();

  function update<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormValues, string>> = {};
    if (!values.name.trim()) next.name = 'Name is required.';
    if (!EMAIL_PATTERN.test(values.email.trim())) next.email = 'Enter a valid email address.';
    if (!values.subject.trim()) next.subject = 'Subject is required.';
    if (!values.message.trim()) next.message = 'Message is required.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!validate()) return;
    mutate(
      {
        name: values.name.trim(),
        email: values.email.trim(),
        subject: values.subject.trim(),
        message: values.message.trim(),
      },
      { onSuccess: () => setValues(EMPTY_VALUES) },
    );
  }

  const inputClassName =
    'focus-visible:ring-brand w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2';
  const errorClassName = 'mt-1 text-xs text-red-600';

  if (isSuccess) {
    return (
      <div role="status" className="flex flex-col items-center gap-3 rounded-2xl border border-gray-100 bg-white px-6 py-12 text-center">
        <CheckCircle2 className="h-10 w-10 text-green-600" aria-hidden="true" />
        <p className="text-base font-semibold text-gray-900">Message sent</p>
        <p className="max-w-sm text-sm text-gray-500">Thanks for reaching out -- our team will get back to you soon.</p>
        <button
          type="button"
          onClick={reset}
          className="focus-visible:ring-brand mt-2 rounded-full border border-gray-200 px-4 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-2xl border border-gray-100 bg-white p-4 sm:p-6" noValidate>
      {isError && <StorefrontErrorState message="Couldn't send your message. Please try again." />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1 block text-sm font-medium text-gray-700">
            Name
          </label>
          <input
            id="contact-name"
            type="text"
            value={values.name}
            maxLength={NAME_MAX}
            onChange={(event) => update('name', event.target.value)}
            className={inputClassName}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? 'contact-name-error' : undefined}
          />
          {errors.name && (
            <p id="contact-name-error" className={errorClassName}>
              {errors.name}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1 block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            id="contact-email"
            type="email"
            value={values.email}
            onChange={(event) => update('email', event.target.value)}
            className={inputClassName}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? 'contact-email-error' : undefined}
          />
          {errors.email && (
            <p id="contact-email-error" className={errorClassName}>
              {errors.email}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="contact-subject" className="mb-1 block text-sm font-medium text-gray-700">
          Subject
        </label>
        <input
          id="contact-subject"
          type="text"
          value={values.subject}
          maxLength={SUBJECT_MAX}
          onChange={(event) => update('subject', event.target.value)}
          className={inputClassName}
          aria-invalid={Boolean(errors.subject)}
          aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
        />
        {errors.subject && (
          <p id="contact-subject-error" className={errorClassName}>
            {errors.subject}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contact-message" className="mb-1 block text-sm font-medium text-gray-700">
          Message
        </label>
        <textarea
          id="contact-message"
          value={values.message}
          maxLength={MESSAGE_MAX}
          onChange={(event) => update('message', event.target.value)}
          rows={5}
          className={inputClassName}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? 'contact-message-error' : undefined}
        />
        {errors.message && (
          <p id="contact-message-error" className={errorClassName}>
            {errors.message}
          </p>
        )}
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isPending}
          className="focus-visible:ring-brand bg-brand text-brand-foreground rounded-full px-5 py-2 text-sm font-semibold hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
        >
          {isPending ? 'Sending…' : 'Send message'}
        </button>
      </div>
    </form>
  );
}
